/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import React from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { handleError } from '@/utils/handelErrorMessage';
import { toast } from 'sonner';

import { useComponents } from '@/hooks/useComponents';
import { ConfirmationDialog } from '@/components/confirmation-alert-dialog';

interface DeleteComponentProps {
  isOpen: boolean;
  onClose: () => void;
  componentName: string;
  onSuccess?: () => void;
}

export const DeleteComponent: React.FC<DeleteComponentProps> = ({
  isOpen,
  onClose,
  componentName,
  onSuccess,
}) => {
  const { messages } = useLanguage();
  const dialogStrings = messages?.ComponentsPage;
  const {
    state: { deletingComponent },
    componentsActions,
  } = useComponents();

  const handleDelete = async () => {
    try {
      if (componentsActions?.deleteComponent) {
        const response = await componentsActions.deleteComponent(componentName);
        if (response) {
          toast.success(dialogStrings?.component_deleted_successfully_msg);
          onSuccess?.();
          onClose();
        }
      } else {
        console.error('deleteComponent is not available in componentsActions');
      }
    } catch (error) {
      console.error('Error deleting component:', error);
      handleError(error, dialogStrings?.error_component_deleted_msg);
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      title={dialogStrings?.delete_component_title}
      description={dialogStrings?.delete_component_description}
      onConfirm={handleDelete}
      isLoading={deletingComponent}
      confirmButtonText={messages?.General?.delete}
      cancelButtonText={messages?.General?.cancel}
    />
  );
};
